import {
  discoveryApiRef,
  fetchApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useMemo } from 'react';

import type {
  ArgoApplication,
  ArgoApplicationAccess,
  ArgoApplicationDetails,
  ArgoApplicationsResponse,
  ArgoAppSet,
  ArgoAppSetsResponse,
  ArgoGeneratedApp,
  ArgoLogLine,
  ArgoResource,
  ArgoResourceAction,
  ArgoResourceDetails,
  ArgoRevisionMetadata,
  ArgoSyncRequest,
} from './types';

interface MicrosoftIdTokenApi {
  getIdToken(): Promise<string>;
}

export class ArgoCdApi {
  constructor(
    private readonly discoveryApi: {
      getBaseUrl(pluginId: string): Promise<string>;
    },
    private readonly fetchApi: {
      fetch(input: RequestInfo, init?: RequestInit): Promise<Response>;
    },
    private readonly microsoftAuthApi: MicrosoftIdTokenApi,
  ) {}

  async applications(): Promise<ArgoApplicationsResponse> {
    return this.request<ArgoApplicationsResponse>('/native/applications');
  }

  async application(
    application: ArgoApplication,
  ): Promise<ArgoApplicationDetails> {
    const response = await this.request<{
      application: ArgoApplicationDetails;
    }>(this.applicationPath(application));
    return response.application;
  }

  async refresh(
    application: ArgoApplication,
    type: 'normal' | 'hard' = 'normal',
  ): Promise<ArgoApplication> {
    const query = new URLSearchParams({ type });
    if (application.appNamespace) {
      query.set('appNamespace', application.appNamespace);
    }
    const response = await this.request<{ application: ArgoApplication }>(
      `${this.applicationPath(application, '/refresh', false)}?${query}`,
      { method: 'POST' },
    );
    return response.application;
  }

  async revisionMetadata(
    application: ArgoApplication,
    revision: string,
  ): Promise<ArgoRevisionMetadata> {
    const query = new URLSearchParams({ revision });
    if (application.appNamespace) {
      query.set('appNamespace', application.appNamespace);
    }
    const response = await this.request<{ metadata: ArgoRevisionMetadata }>(
      `${this.applicationPath(
        application,
        '/revision-metadata',
        false,
      )}?${query}`,
    );
    return response.metadata;
  }

  async logs(
    application: ArgoApplication,
    params: {
      podName: string;
      namespace: string;
      container?: string;
      tailLines?: number;
      sinceSeconds?: number;
      previous?: boolean;
    },
  ): Promise<ArgoLogLine[]> {
    const query = new URLSearchParams({
      podName: params.podName,
      namespace: params.namespace,
    });
    if (application.appNamespace) {
      query.set('appNamespace', application.appNamespace);
    }
    if (params.container) query.set('container', params.container);
    if (params.tailLines) query.set('tailLines', String(params.tailLines));
    if (params.sinceSeconds) {
      query.set('sinceSeconds', String(params.sinceSeconds));
    }
    if (params.previous) query.set('previous', 'true');
    const response = await this.request<{ lines: ArgoLogLine[] }>(
      `${this.applicationPath(application, '/logs', false)}?${query}`,
    );
    return response.lines;
  }

  async rollback(
    application: ArgoApplication,
    id: number,
    prune: boolean,
  ): Promise<void> {
    await this.request(this.applicationPath(application, '/rollback'), {
      method: 'POST',
      body: JSON.stringify({ id, prune }),
    });
  }

  async terminateOperation(application: ArgoApplication): Promise<void> {
    await this.request(this.applicationPath(application, '/operation'), {
      method: 'DELETE',
    });
  }

  async resourceActions(
    application: ArgoApplication,
    resource: ArgoResource,
  ): Promise<ArgoResourceAction[]> {
    const response = await this.request<{ actions: ArgoResourceAction[] }>(
      `${this.applicationPath(
        application,
        '/resource/actions',
        false,
      )}?${this.resourceQuery(application, resource)}`,
    );
    return response.actions;
  }

  async runResourceAction(
    application: ArgoApplication,
    resource: ArgoResource,
    action: string,
  ): Promise<void> {
    await this.request(
      `${this.applicationPath(
        application,
        '/resource/actions',
        false,
      )}?${this.resourceQuery(application, resource)}`,
      { method: 'POST', body: JSON.stringify({ action }) },
    );
  }

  async patchResource(
    application: ArgoApplication,
    resource: ArgoResource,
    patch: string,
    patchType: string,
  ): Promise<void> {
    await this.request(
      `${this.applicationPath(
        application,
        '/resource/patch',
        false,
      )}?${this.resourceQuery(application, resource)}`,
      { method: 'POST', body: JSON.stringify({ patch, patchType }) },
    );
  }

  async deleteResource(
    application: ArgoApplication,
    resource: ArgoResource,
    options: { force: boolean; orphan: boolean },
  ): Promise<void> {
    const query = this.resourceQuery(application, resource);
    if (options.force) query.set('force', 'true');
    if (options.orphan) query.set('orphan', 'true');
    await this.request(
      `${this.applicationPath(application, '/resource', false)}?${query}`,
      { method: 'DELETE' },
    );
  }

  async applicationSets(): Promise<ArgoAppSetsResponse> {
    return this.request<ArgoAppSetsResponse>('/native/applicationsets');
  }

  async appSetGenerate(appSet: ArgoAppSet): Promise<ArgoGeneratedApp[]> {
    const namespace = appSet.namespace
      ? `?appsetNamespace=${encodeURIComponent(appSet.namespace)}`
      : '';
    const response = await this.request<{ applications: ArgoGeneratedApp[] }>(
      `/native/applicationsets/${encodeURIComponent(
        appSet.instance,
      )}/${encodeURIComponent(appSet.name)}/generate${namespace}`,
      { method: 'POST' },
    );
    return response.applications;
  }

  private resourceQuery(
    application: ArgoApplication,
    resource: ArgoResource,
  ): URLSearchParams {
    const query = new URLSearchParams({
      resourceName: resource.name ?? '',
      version: resource.version ?? '',
      kind: resource.kind ?? '',
      group: resource.group ?? '',
    });
    if (resource.namespace) query.set('namespace', resource.namespace);
    if (application.appNamespace) {
      query.set('appNamespace', application.appNamespace);
    }
    return query;
  }

  async access(application: ArgoApplication): Promise<ArgoApplicationAccess> {
    const project = `project=${encodeURIComponent(application.project)}`;
    const namespace = application.appNamespace
      ? `&appNamespace=${encodeURIComponent(application.appNamespace)}`
      : '';
    const response = await this.request<{ access: ArgoApplicationAccess }>(
      `${this.applicationPath(
        application,
        '/access',
        false,
      )}?${project}${namespace}`,
    );
    return response.access;
  }

  async sync(
    application: ArgoApplication,
    request: ArgoSyncRequest,
  ): Promise<void> {
    await this.request(this.applicationPath(application, '/sync'), {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async resource(
    application: ArgoApplication,
    resource: ArgoResource,
  ): Promise<ArgoResourceDetails> {
    const query = new URLSearchParams();
    if (application.appNamespace) {
      query.set('appNamespace', application.appNamespace);
    }
    if (resource.namespace) query.set('namespace', resource.namespace);
    if (resource.name) query.set('resourceName', resource.name);
    if (resource.version) query.set('version', resource.version);
    if (resource.kind) query.set('kind', resource.kind);
    query.set('group', resource.group ?? '');
    if (resource.uid) query.set('uid', resource.uid);
    const response = await this.request<{ resource: ArgoResourceDetails }>(
      `${this.applicationPath(application, '/resource', false)}?${query}`,
    );
    return response.resource;
  }

  private applicationPath(
    application: ArgoApplication,
    action = '',
    includeNamespace = true,
  ): string {
    const namespace =
      includeNamespace && application.appNamespace
        ? `?appNamespace=${encodeURIComponent(application.appNamespace)}`
        : '';
    return `/native/applications/${encodeURIComponent(
      application.instance,
    )}/${encodeURIComponent(application.name)}${action}${namespace}`;
  }

  private async request<T = unknown>(
    path: string,
    init: RequestInit = {},
  ): Promise<T> {
    const [baseUrl, idToken] = await Promise.all([
      this.discoveryApi.getBaseUrl('argocd'),
      this.microsoftAuthApi.getIdToken(),
    ]);
    if (!idToken) throw new Error('Microsoft Entra login is required');

    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'X-Argo-Authorization': `Bearer ${idToken}`,
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...init.headers,
      },
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new Error(
        typeof body.error === 'string'
          ? body.error
          : `Argo CD request failed (${response.status})`,
      );
    }
    if (response.status === 204) return undefined as T;
    return response.json() as Promise<T>;
  }
}

export function useArgoCdApi(): ArgoCdApi {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const microsoftAuthApi = useApi(microsoftAuthApiRef);

  return useMemo(
    () => new ArgoCdApi(discoveryApi, fetchApi, microsoftAuthApi),
    [discoveryApi, fetchApi, microsoftAuthApi],
  );
}
